/**
 * The guided setup (implantação): seven stages from the contract to the first finished
 * conversation. Each step is detected by the API from what exists in the workspace, never ticked
 * by hand; recommended and optional steps can be skipped and taken up again later.
 */
import type { TemplateDraft } from "./templates.ts";

export type StepLevel = "required" | "recommended" | "optional";

export const ONBOARDING_STAGES = [
  { key: "welcome", minutes: 3, steps: [{ key: "questionnaire", level: "recommended" }] },
  {
    key: "whatsapp",
    minutes: 10,
    steps: [
      { key: "connect", level: "required" },
      { key: "payment", level: "recommended" },
    ],
  },
  {
    key: "team",
    minutes: 10,
    steps: [
      { key: "invite", level: "required" },
      { key: "queues", level: "recommended" },
      { key: "hours", level: "recommended" },
    ],
  },
  {
    key: "organization",
    minutes: 10,
    steps: [
      { key: "outcomes", level: "required" },
      { key: "snippets", level: "recommended" },
      { key: "autoclose", level: "recommended" },
      { key: "tags", level: "optional" },
    ],
  },
  {
    key: "entry",
    minutes: 15,
    steps: [
      { key: "first_contact", level: "required" },
      { key: "after_hours", level: "recommended" },
      { key: "assistant", level: "optional" },
      { key: "skills", level: "optional" },
    ],
  },
  {
    key: "outbound",
    minutes: 15,
    steps: [
      { key: "templates", level: "recommended" },
      { key: "optout", level: "recommended" },
      { key: "budget", level: "recommended" },
    ],
  },
  {
    key: "golive",
    minutes: 10,
    steps: [
      { key: "rehearsal", level: "required" },
      { key: "tour", level: "recommended" },
      { key: "live", level: "required" },
    ],
  },
] as const satisfies ReadonlyArray<{
  key: string;
  minutes: number;
  steps: ReadonlyArray<{ key: string; level: StepLevel }>;
}>;

export type StageKey = (typeof ONBOARDING_STAGES)[number]["key"];
type StageSteps<S> = S extends { key: infer K extends string; steps: ReadonlyArray<infer T> }
  ? T extends { key: infer J extends string }
    ? `${K}.${J}`
    : never
  : never;
/** "whatsapp.connect", "team.invite"… */
export type StepKey = StageSteps<(typeof ONBOARDING_STAGES)[number]>;

export const STEP_KEYS: StepKey[] = ONBOARDING_STAGES.flatMap((s) =>
  s.steps.map((st) => `${s.key}.${st.key}` as StepKey),
);

export function stepLevel(key: StepKey): StepLevel {
  const [stage, step] = key.split(".");
  return (ONBOARDING_STAGES.find((s) => s.key === stage)?.steps.find((s) => s.key === step)
    ?.level ?? "optional") as StepLevel;
}

// ---- questionnaire ----

export const PURPOSES = ["sell", "support", "tech_support", "billing", "scheduling"] as const;
export type Purpose = (typeof PURPOSES)[number];
export const SEGMENTS = [
  "real_estate",
  "education",
  "health",
  "retail",
  "services",
  "finance",
  "automotive",
  "travel",
  "other",
] as const;
export type Segment = (typeof SEGMENTS)[number];
export const TEAM_SIZES = ["solo", "small", "medium", "large"] as const;
export type TeamSize = (typeof TEAM_SIZES)[number];
export const WEEKDAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"] as const;
/**
 * Which number goes on the official WhatsApp (decision 0018, d12): the WhatsApp Business app's
 * number, another free number of the company, or one rented through DoneTalk.
 */
export const NUMBER_CHOICES = ["app", "own", "rent"] as const;
export type NumberChoice = (typeof NUMBER_CHOICES)[number];
export type Weekday = (typeof WEEKDAYS)[number];

export interface HoursBlock {
  days: Weekday[];
  from: string;
  to: string;
}

export interface OnboardingAnswers {
  purposes: Purpose[];
  segment: Segment | null;
  teamSize: TeamSize;
  hours: HoursBlock[];
  website: string | null;
  /** Answered from 2026-09-29 on; older journeys have none. */
  number?: NumberChoice | null;
}

/** The questionnaire's hours as directory.business_hours.weekly. */
export function weeklyFromBlocks(
  blocks: HoursBlock[],
): Record<Weekday, Array<{ from: string; to: string }>> {
  const weekly = Object.fromEntries(WEEKDAYS.map((d) => [d, []])) as unknown as Record<
    Weekday,
    Array<{ from: string; to: string }>
  >;
  for (const b of blocks) for (const d of b.days) weekly[d].push({ from: b.from, to: b.to });
  return weekly;
}

// ---- proposals: what the journey suggests from the answers; nothing is applied without approval ----

export type OutcomeCategoryName = "success" | "neutral" | "negative" | "invalid";

export interface ProposedTeam {
  /** Stable within the proposal: the welcome menu points at teams by this key. */
  key: string;
  name: string;
}

export interface ProposedOutcome {
  /** An existing outcome's code (relabeled when the label differs) or a new code. */
  code: string;
  label: string;
  category: OutcomeCategoryName;
}

export interface ProposedSnippet {
  shortcut: string;
  title: string;
  body: string;
}

export interface OnboardingProposals {
  teams: ProposedTeam[];
  outcomes: ProposedOutcome[];
  snippets: ProposedSnippet[];
  tags: Array<{ name: string; color: string }>;
  autoClose: { noReplyHours: number; closeAs: string; windowExpired: boolean };
  welcome: { message: string; options: Array<{ title: string; team: string }> };
  afterHours: string;
  templates: TemplateDraft[];
  /** "presets" come from the segment and purposes; "ai" were adjusted by AI, with the site if given. */
  source: "presets" | "ai";
}

// ---- state returned by the API ----

export type StepStatus = "done" | "skipped" | "todo";

export interface OnboardingStep {
  key: StepKey;
  level: StepLevel;
  status: StepStatus;
  /** What was detected, for the step's line in the hub: the number, how many snippets… */
  detail: Record<string, unknown>;
}

export interface OnboardingStage {
  key: StageKey;
  minutes: number;
  done: boolean;
  steps: OnboardingStep[];
}

export interface RehearsalProgress {
  code: string | null;
  link: string | null;
  address: string | null;
  conversationId: string | null;
  /** An open conversation from the same phone kept the code from starting a new one. */
  blockedByConversationId: string | null;
  received: boolean;
  automated: boolean | null;
  queued: boolean;
  taken: boolean;
  replied: boolean;
  closed: boolean;
  outcome: string | null;
  minutes: number | null;
}

export interface OnboardingState {
  startedAt: string | null;
  welcomedAt: string | null;
  answers: OnboardingAnswers | null;
  proposals: OnboardingProposals | null;
  liveAt: string | null;
  stages: OnboardingStage[];
  current: StageKey | null;
  stagesDone: number;
  /** Minutes of work left in the stages not done. */
  minutesLeft: number;
  /** Every required step except going live itself is done. */
  canGoLive: boolean;
  rehearsal: RehearsalProgress;
}

/** A stage is done when its required steps are done and its recommended ones done or skipped. */
export function stageDone(steps: Array<Pick<OnboardingStep, "level" | "status">>): boolean {
  return steps.every((s) =>
    s.level === "required"
      ? s.status === "done"
      : s.level === "recommended"
        ? s.status !== "todo"
        : true,
  );
}
